/**
 * Recruiter Copilot — protocol constants.
 * Integration guide rev 1.2, protocol version 1.
 *
 * Paths live here because the two surfaces disagree about trailing slashes:
 * copilot REST routes are registered WITHOUT one, the socket route REQUIRES
 * one. Both failure modes are silent (an APPEND_SLASH redirect that drops the
 * Authorization header, or a handshake that resolves to nothing), so they are
 * pinned in a single file rather than written out at each call site.
 *
 * This module is deliberately dependency-free so the reducer tests can import
 * it without pulling in the app.
 */

export const COPILOT_PROTOCOL_VERSION = 1;

/** REST paths, relative to the axios instance baseURL. No trailing slashes. */
export const COPILOT_REST = {
  status: "/copilot/status",
  ticket: "/copilot/ticket",
  voiceToken: "/copilot/voice/token",
  voiceUsage: "/copilot/voice/usage",
  sessions: "/copilot/sessions",
  session: (sessionId: string) => `/copilot/sessions/${sessionId}`,
  sessionDelete: (sessionId: string) => `/copilot/sessions/${sessionId}/delete`,
  record: (kind: string, recordId: string | number) =>
    `/copilot/records/${kind}/${recordId}`,
  report: (messageId: string) => `/copilot/messages/${messageId}/report`,
  reportLink: (messageId: string) =>
    `/copilot/messages/${messageId}/report-link`,
  documentLink: (messageId: string) =>
    `/copilot/messages/${messageId}/document-link`,
  feedback: (messageId: string) => `/copilot/messages/${messageId}/feedback`,
  /** COP-12: Hairo questions and answers logged against one candidate. */
  candidateHistory: (candidateId: string | number) =>
    `/copilot/candidates/${candidateId}/history`,
  /** POST multipart `file` (read before the response) / GET the session's files. */
  sessionFiles: (sessionId: string) => `/copilot/sessions/${sessionId}/files`,
  file: (fileId: string) => `/copilot/files/${fileId}`,
} as const;

/** The literal session id that opens a new conversation. */
export const NEW_SESSION = "new";

/**
 * Opening Hairo on a subject continues its latest chat only if that chat was
 * active this recently; older ones start fresh (still reachable from History),
 * which bounds stale answers and how long a single session grows.
 */
export const RESUME_WINDOW_MS = 24 * 60 * 60 * 1000;

export type SubjectKind = "job" | "application" | "requirement" | "client";

/** Subject kind to the query parameter the socket expects. */
export const SUBJECT_PARAM: Record<SubjectKind, string> = {
  job: "job_id",
  application: "application_id",
  requirement: "requirement_id",
  client: "client_id",
};

export interface CopilotSubject {
  kind: SubjectKind;
  id: string | number;
}

/**
 * Normalise a socket base to a ws/wss scheme.
 * An HTTPS page blocks a plaintext socket as mixed content before the request
 * is made, so a plaintext socket base is upgraded rather than left to fail at runtime.
 */
export function toWebSocketBase(
  base: string,
  /** Overridable so the upgrade rule can be tested without touching location. */
  securePage: boolean = typeof window !== "undefined" &&
    window.location?.protocol === "https:"
): string {
  const trimmed = (base || "").replace(/\/+$/, "");
  if (/^https:\/\//i.test(trimmed)) return trimmed.replace(/^https:/i, "wss:");
  if (/^http:\/\//i.test(trimmed)) {
    return trimmed.replace(/^http:/i, securePage ? "wss:" : "ws:");
  }
  if (securePage && /^ws:\/\//i.test(trimmed)) {
    return trimmed.replace(/^ws:/i, "wss:");
  }
  return trimmed;
}

export interface SocketUrlOptions {
  /** VITE_WEBSOCKET_URL — already ends in /ws. */
  socketBase: string;
  sessionId?: string;
  /** Preferred credential: a short-lived ticket from POST /copilot/ticket. */
  ticket?: string | null;
  /** Platform token — dev/Postman only, never production. */
  token?: string | null;
  subject?: CopilotSubject | null;
  /** Page the user opened Hairo from (no record behind it), e.g. Team Performance + its period. */
  pageContext?: CopilotPageContext | null;
  /** Resuming a chat: its subject becomes `subject` (none when null), the page now. */
  followPage?: boolean;
}

export interface CopilotPageContext {
  page: string;
  start_date?: string;
  end_date?: string;
}

/** Build the socket URL. The trailing slash after the session id is required. */
export function buildCopilotSocketUrl({
  socketBase,
  sessionId = NEW_SESSION,
  ticket,
  token,
  subject,
  pageContext,
  followPage = false,
}: SocketUrlOptions): string {
  const base = toWebSocketBase(socketBase);
  const params = new URLSearchParams();
  if (ticket) params.set("ticket", ticket);
  else if (token) params.set("token", token);

  if (subject?.kind && subject.id !== null && subject.id !== undefined) {
    const param = SUBJECT_PARAM[subject.kind];
    if (param) params.set(param, String(subject.id));
  }

  // A hint for the model only; the server allowlists the page and re-checks scope.
  if (pageContext?.page) {
    params.set("page", pageContext.page);
    if (pageContext.start_date && pageContext.end_date) {
      params.set("start_date", pageContext.start_date);
      params.set("end_date", pageContext.end_date);
    }
  }

  if (followPage && sessionId !== NEW_SESSION) params.set("follow_page", "1");

  const query = params.toString();
  return `${base}/copilot/${sessionId}/${query ? `?${query}` : ""}`;
}

/* ── Close codes ─────────────────────────────────────────────────────────── */

export const CLOSE_CODES = {
  NORMAL: 1000,
  GOING_AWAY: 1001,
  ABNORMAL: 1006,
  /** bad session id, or a subject outside this user's scope */
  BAD_SUBJECT: 4400,
  /** credential missing or invalid at connect time */
  UNAUTHORIZED: 4401,
  /** socket ticket expired or unverifiable — NOT a credential failure */
  TICKET_INVALID: 4402,
  /** no tenant scope (jobseeker role, or no company membership) */
  FORBIDDEN: 4403,
  /** this session was opened on another socket */
  SUPERSEDED: 4409,
  /** feature disabled or unconfigured */
  UNAVAILABLE: 4503,
  /** the account's plan does not include Hairo (COP-11) */
  PLAN_REQUIRED: 4412,
} as const;

export type CloseAction =
  /** mint a fresh ticket and reconnect without telling the user */
  | "retry-ticket"
  /** transport drop — reconnect with backoff */
  | "reconnect"
  /** genuine credential failure — hand off to the app's session handler */
  | "logout"
  /** the feature is not available here — hide the entry point */
  | "hide"
  /** show the message and stay closed; never auto-reconnect */
  | "notify"
  /** clean close — reconnect only on a user action */
  | "idle";

/** Fallback upsell when the server's own wording is not available. */
export const PLAN_REQUIRED_MESSAGE =
  "Hairo is included with the Business plan and above. Upgrade your plan to ask Hairo about your candidates and interviews.";

/** Where the upsell sends the recruiter. */
export const PLANS_PATH = "/dashboard/subscription";

export interface CloseCodePolicy {
  action: CloseAction;
  message: string;
}

const CLOSE_POLICY: Record<number, CloseCodePolicy> = {
  [CLOSE_CODES.NORMAL]: { action: "idle", message: "" },
  [CLOSE_CODES.GOING_AWAY]: { action: "reconnect", message: "" },
  [CLOSE_CODES.ABNORMAL]: { action: "reconnect", message: "" },
  [CLOSE_CODES.BAD_SUBJECT]: {
    action: "notify",
    message: "That job isn't available to you.",
  },
  [CLOSE_CODES.UNAUTHORIZED]: {
    action: "logout",
    message: "Your session expired. Please sign in again.",
  },
  [CLOSE_CODES.TICKET_INVALID]: {
    action: "retry-ticket",
    message: "",
  },
  [CLOSE_CODES.FORBIDDEN]: {
    action: "hide",
    message: "The copilot isn't available for this account.",
  },
  [CLOSE_CODES.SUPERSEDED]: {
    action: "notify",
    message: "This conversation is open in another tab.",
  },
  [CLOSE_CODES.UNAVAILABLE]: {
    action: "hide",
    message: "The copilot is not enabled on this environment.",
  },
  // The server sends a readable upsell in an `error` frame (code
  // plan_required) just before this close; the panel prefers that text.
  [CLOSE_CODES.PLAN_REQUIRED]: {
    action: "hide",
    message: PLAN_REQUIRED_MESSAGE,
  },
};

/**
 * Policy for a close code.
 * An unknown application code (4000+) is deliberately NOT reconnected: the
 * server closed us on purpose, and a reconnect loop is the worst failure here.
 */
export function closePolicyFor(code: number): CloseCodePolicy {
  const known = CLOSE_POLICY[code];
  if (known) return known;
  return code >= 4000
    ? { action: "notify", message: "Connection closed." }
    : { action: "reconnect", message: "" };
}

/* ── Error frame codes ───────────────────────────────────────────────────── */

export const ERROR_CODES = {
  BUSY: "busy",
  EMPTY: "empty",
  BAD_FRAME: "bad_frame",
  NO_QUESTION: "no_question",
  BAD_RETRY: "bad_retry",
  BAD_PROPOSAL: "bad_proposal",
  REFUSED: "refused",
  WRITE_FAILED: "write_failed",
  DISCARDED: "discarded",
  STOPPED: "stopped",
  TURN_FAILED: "turn_failed",
  ALREADY_STARTED: "already_started",
  REVOKED: "revoked",
  SUPERSEDED: "superseded",
  BAD_REQUEST: "bad_request",
  START_FAILED: "start_failed",
  PLAN_REQUIRED: "plan_required",
  // The chat could not follow the page yet (a turn or task is running); the
  // panel sends the page subject again once it ends.
  SUBJECT_BUSY: "subject_busy",
  BAD_SUBJECT: "bad_subject",
  // A chat frame's attachment_ids were refused (the message is not sent).
  BAD_ATTACHMENT: "bad_attachment",
  FILE_NOT_READY: "file_not_ready",
  FILES_DISABLED: "files_disabled",
} as const;

/** Client bugs and acknowledgements the user never needs to see. */
export const SILENT_ERROR_CODES: ReadonlySet<string> = new Set([
  ERROR_CODES.EMPTY,
  ERROR_CODES.NO_QUESTION,
  ERROR_CODES.BAD_FRAME,
  ERROR_CODES.ALREADY_STARTED,
  // Shown as the gated panel itself, not as a notice on top of it.
  ERROR_CODES.PLAN_REQUIRED,
  // Following the page is best effort; the header just keeps the old subject.
  ERROR_CODES.SUBJECT_BUSY,
  ERROR_CODES.BAD_SUBJECT,
  // Acknowledgements of what the user just did: the transcript already shows
  // the stop, and a discarded proposal closes its dialog quietly (guide §3).
  ERROR_CODES.STOPPED,
  ERROR_CODES.DISCARDED,
]);

/** Any of these ends a pending proposal dialog — there is no second proposal frame. */
export const PROPOSAL_CLOSING_ERROR_CODES: ReadonlySet<string> = new Set([
  ERROR_CODES.BAD_PROPOSAL,
  ERROR_CODES.REFUSED,
  ERROR_CODES.WRITE_FAILED,
  ERROR_CODES.DISCARDED,
]);

/* ── Wire enums ──────────────────────────────────────────────────────────── */

/** Documented reasoning kinds. The set may grow within v1 — never drop a frame. */
export const THINKING_KINDS = [
  "decision",
  "subject",
  "retrieval",
  "search",
  "aggregate",
  "trigger",
  "reconcile",
  "question",
  "checklist",
  "grounding",
  "fairness",
  "proposal",
  "note",
  "error",
  // One tool call, narrated in plain words.
  "step",
] as const;

export type ThinkingKind = (typeof THINKING_KINDS)[number];

const THINKING_KIND_SET: ReadonlySet<string> = new Set(THINKING_KINDS);

export const isKnownThinkingKind = (kind: string): boolean =>
  THINKING_KIND_SET.has(kind);

export const CHAT_MODES = ["lookup", "question", "report"] as const;
export const RETRY_KINDS = ["redo", "elaborate", "simplify"] as const;

/** Record kinds a citation can point at. record_id is the platform PK in every case. */
export const RECORD_KINDS = [
  "job",
  "application",
  "candidate",
  "interview",
  "assessment",
  "requirement",
  "client",
  "note",
  "lead_candidate",
  // An EmailSend row (Hairo's email_activity tool).
  "email",
  // A file the user attached to the conversation.
  "file",
] as const;

export type RecordKind = (typeof RECORD_KINDS)[number];

/** Grounding severities, worst first. */
export const GROUNDING_SEVERITIES: Record<string, string> = {
  OUT_OF_SCOPE: "Referred to a record from another account",
  NONEXISTENT: "Referred to a record that does not exist",
  UNPROJECTED: "Referred to a detail the record does not have",
  UNREAD: "Referred to a record that was not checked",
  UNCITED_CLAIM: "A claim about a person or number without a source",
  STALE: "The record changed after it was checked",
};

/* ── Citation markers ────────────────────────────────────────────────────── */

/** Canonical marker grammar, from the backend's own extractor. */
export const CITATION_MARKER_SOURCE = "\\[record:([a-z_]+):([0-9]+)\\]";

/** Fresh regex per call — a shared global regex carries lastIndex between uses. */
export const citationPattern = (flags = "g"): RegExp =>
  new RegExp(CITATION_MARKER_SOURCE, flags);

/**
 * Anything shaped like a marker, whatever its id. Used only to catch markers
 * damaged on the way to us — e.g. contact masking turning
 * `[record:interview:489427071832]` into `[record:interview:********1832]` —
 * so they render as unresolved chips instead of raw text.
 */
export const CITATION_MARKER_LOOSE_SOURCE =
  "\\[record:([a-z_]+):([^\\]\\s]{1,64})\\]";

export const looseCitationPattern = (flags = "g"): RegExp =>
  new RegExp(CITATION_MARKER_LOOSE_SOURCE, flags);

const CANONICAL_ID = /^[0-9]+$/;

/** True when an id matches the canonical grammar (digits only). */
export const isCanonicalRecordId = (id: string): boolean => CANONICAL_ID.test(id);

/* ── Capabilities ────────────────────────────────────────────────────────── */

/** Proposable actions the copilot can offer today. Used for labels only. */
export const COPILOT_ACTIONS = {
  ADD_NOTE: "add_note",
  SET_APPLICATION_STATUS: "set_application_status",
} as const;

/** Hairo posts one job itself (propose_write post_job). */
export const POST_JOB_CAPABILITY = "post_job";

export const capabilityKey = (action: string): string => `can_${action}`;

/**
 * Whether the user may be offered an action.
 * Unknown or missing capability is false: the server computes these from the
 * real permission set, so absence means "do not offer", never "assume yes".
 */
export function hasCapability(
  scope: { capabilities?: Record<string, boolean> } | null | undefined,
  action: string
): boolean {
  return scope?.capabilities?.[capabilityKey(action)] === true;
}

/* ── Entry point gating ──────────────────────────────────────────────────── */

/**
 * Roles with no copilot entry point.
 * 2 = Jobseeker — the server refuses the socket with 4403.
 * 4 = Client — product decision for v1: an unverified answer about named
 *     candidates must not reach an external third party. The server still
 *     scopes role 4 read-only, so this is a UI gate, not the only control.
 * See src/constants/roleConfig.ts for the role ids.
 */
export const COPILOT_HIDDEN_ROLES: ReadonlySet<number> = new Set([2, 4]);

/**
 * Roles that DO get the entry point: 1 Company/Recruiter, 3 User (invited
 * team member), 5 Agency. The gate checks this allow-list, not the hidden
 * list, so an unknown or still-loading role is denied. While the role is
 * rehydrating, the router picks a route set by URL, and a client on a
 * recruiter-shaped link (e.g. a shared Results profile) can briefly be inside
 * the recruiter routes, where the provider lives.
 */
export const COPILOT_ALLOWED_ROLES: ReadonlySet<number> = new Set([1, 3, 5]);

/* ── Tunables ────────────────────────────────────────────────────────────── */

/** Proxies typically close idle sockets at 60s. */
export const PING_INTERVAL_MS = 25_000;
/** ready.history is capped server-side. */
export const HISTORY_LIMIT = 40;
/** The most recent N history entries carry gates/records/has_report. */
export const HISTORY_EVIDENCE_TAIL = 5;
/** Transcript page size; the server's max is 200. */
export const TRANSCRIPT_PAGE_LIMIT = 100;
/**
 * Idle close: with nothing running and nothing waiting on the user, the socket
 * is closed after this long with no frames either way (pings don't count), and
 * the next send reopens the same session. Sooner when the tab is hidden.
 */
export const IDLE_CLOSE_MS = 5 * 60_000;
export const HIDDEN_IDLE_CLOSE_MS = 60_000;
/** How often the idle check runs. */
export const IDLE_CHECK_MS = 15_000;
/** Reconnect backoff for transport drops. */
export const RECONNECT_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 20_000];
